import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';
import { DollarSign, Plus, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function ARReconciliationPage() {
  const [data, setData] = useState({ rows: [], totals: { sold: 0, collected: 0, outstanding: 0, patients: 0 } });
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('outstanding');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), payment_method: '', notes: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (locationFilter !== 'all') params.location_id = locationFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [arRes, locRes] = await Promise.all([
        axios.get(`${API}/ar/reconciliation`, { params }),
        axios.get(`${API}/locations`),
      ]);
      setData(arRes.data);
      setLocations(locRes.data);
    } catch (err) {
      toast.error('Failed to load A/R data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [locationFilter, statusFilter]);

  const fetchPayments = async (sale_id) => {
    try {
      const r = await axios.get(`${API}/ar/payments/${sale_id}`);
      setPayments(r.data);
    } catch { setPayments([]); }
  };

  const openDetails = async (row) => {
    setSelected(row);
    setPayForm({ amount: row.outstanding_amount.toString(), payment_date: new Date().toISOString().slice(0, 10), payment_method: '', notes: '' });
    await fetchPayments(row.sale_id);
  };

  const recordPayment = async () => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    try {
      await axios.post(`${API}/ar/payments`, { sale_id: selected.sale_id, ...payForm, amount: amt });
      toast.success(`Payment of ${fmtMoney(amt)} recorded`);
      setPayForm({ amount: '', payment_date: new Date().toISOString().slice(0, 10), payment_method: '', notes: '' });
      await fetchPayments(selected.sale_id);
      await fetchData();
      const updated = data.rows.find((r) => r.sale_id === selected.sale_id);
      if (updated) setSelected({ ...updated, outstanding_amount: updated.outstanding_amount - amt, collected_amount: updated.collected_amount + amt });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/ar/payments/${pid}`);
      toast.success('Payment deleted');
      await fetchPayments(selected.sale_id);
      await fetchData();
    } catch { toast.error('Failed'); }
  };

  const collectionPct = data.totals.sold > 0 ? (data.totals.collected / data.totals.sold) * 100 : 0;

  const sortedRows = useMemo(() => {
    return [...data.rows].sort((a, b) => b.outstanding_amount - a.outstanding_amount);
  }, [data.rows]);

  return (
    <div data-testid="ar-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-3xl tracking-tight" style={{ color: '#0E0E0E' }}>A/R Reconciliation</h2>
          <p className="text-sm text-stone-500 mt-1">Per-patient sold vs collected vs outstanding.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[180px] h-10 bg-white" data-testid="ar-location-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-10 bg-white" data-testid="ar-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="outstanding">Outstanding</SelectItem>
              <SelectItem value="paid">Paid in full</SelectItem>
              <SelectItem value="all">All patients</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Sold', value: fmtMoney(data.totals.sold), accent: '#0E0E0E' },
          { label: 'Total Collected', value: fmtMoney(data.totals.collected), accent: '#15803D' },
          { label: 'Total Outstanding', value: fmtMoney(data.totals.outstanding), accent: '#9F1239' },
          { label: 'Patients', value: data.totals.patients.toString(), accent: '#C9A961' },
        ].map((c) => (
          <div key={c.label} className="p-5 rounded-lg bg-white border" style={{ borderColor: 'rgba(14,14,14,0.08)', borderLeft: `2px solid ${c.accent}` }}>
            <p className="text-[10px] uppercase tracking-widest text-stone-500">{c.label}</p>
            <p className="font-heading text-3xl mt-1" style={{ color: c.accent }}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Collection progress bar */}
      <div className="p-4 rounded-lg bg-white border" style={{ borderColor: 'rgba(14,14,14,0.08)' }}>
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-stone-500 uppercase tracking-widest">Collection Rate</span>
          <span className="font-medium" style={{ color: '#0E0E0E' }}>{collectionPct.toFixed(1)}%</span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${Math.min(collectionPct, 100)}%`, background: 'linear-gradient(90deg, #8A6A2A, #C9A961)' }} />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg overflow-hidden bg-white border" style={{ borderColor: 'rgba(14,14,14,0.08)' }}>
        <table className="w-full text-sm">
          <thead style={{ background: '#F8F5EE' }}>
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-stone-600">Patient</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-stone-600">Location</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-stone-600">Treatment</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-stone-600">Sold</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-stone-600">Collected</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-stone-600">Outstanding</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-stone-500 text-xs">Loading…</td></tr>}
            {!loading && sortedRows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-stone-400 text-xs">No matching patients</td></tr>
            )}
            {sortedRows.map((row) => (
              <tr key={row.sale_id} className="border-t hover:bg-stone-50 cursor-pointer" style={{ borderColor: 'rgba(14,14,14,0.05)' }} onClick={() => openDetails(row)} data-testid={`ar-row-${row.sale_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium" style={{ color: '#0E0E0E' }}>{row.patient_first_name} {row.patient_last_name}</div>
                  <div className="text-xs text-stone-500">{row.submission_date}</div>
                </td>
                <td className="px-4 py-3 text-stone-600">{row.location_name}</td>
                <td className="px-4 py-3 text-stone-600 text-xs">{row.treatment_sold}</td>
                <td className="px-4 py-3 text-right font-medium" style={{ color: '#0E0E0E' }}>{fmtMoney(row.sold_amount)}</td>
                <td className="px-4 py-3 text-right" style={{ color: '#15803D' }}>{fmtMoney(row.collected_amount)}</td>
                <td className="px-4 py-3 text-right font-semibold" style={{ color: row.outstanding_amount > 0 ? '#9F1239' : '#15803D' }}>{fmtMoney(row.outstanding_amount)}</td>
                <td className="px-4 py-3 text-right">
                  {row.outstanding_amount > 0 && (
                    <button className="text-xs px-2 py-1 rounded border inline-flex items-center gap-1" style={{ background: '#0E0E0E', color: '#C9A961' }}>
                      <Plus size={11} /> Payment
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl" data-testid="ar-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{selected.patient_first_name} {selected.patient_last_name}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded border bg-stone-50">
                <p className="text-[10px] uppercase text-stone-500 tracking-widest">Sold</p>
                <p className="font-heading text-xl">{fmtMoney(selected.sold_amount)}</p>
              </div>
              <div className="p-3 rounded border bg-emerald-50">
                <p className="text-[10px] uppercase text-stone-500 tracking-widest">Collected</p>
                <p className="font-heading text-xl text-emerald-700">{fmtMoney(selected.collected_amount)}</p>
              </div>
              <div className="p-3 rounded border bg-rose-50">
                <p className="text-[10px] uppercase text-stone-500 tracking-widest">Outstanding</p>
                <p className="font-heading text-xl text-rose-700">{fmtMoney(selected.outstanding_amount)}</p>
              </div>
            </div>

            {selected.outstanding_amount > 0 && (
              <div className="p-3 rounded border bg-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-2">Record Payment</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Amount</Label>
                    <Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="ar-payment-amount" /></div>
                  <div><Label className="text-xs">Date</Label>
                    <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
                  <div><Label className="text-xs">Method</Label>
                    <Input placeholder="e.g. CareCredit" value={payForm.payment_method} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} /></div>
                  <div><Label className="text-xs">Notes</Label>
                    <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button onClick={recordPayment} data-testid="ar-record-payment-btn" style={{ background: '#0E0E0E', color: '#C9A961' }} className="gap-2"><DollarSign size={14} /> Record</Button>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-2 mt-2">Payment history</p>
              {payments.length === 0 && (
                <p className="text-xs text-stone-400 py-3">
                  {selected.initial_collected > 0 ? `Initial ${fmtMoney(selected.initial_collected)} collected at sale. No subsequent payments.` : 'No payments recorded.'}
                </p>
              )}
              {payments.length > 0 && (
                <div className="space-y-1">
                  {selected.initial_collected > 0 && (
                    <div className="flex justify-between text-xs px-2 py-1.5 rounded bg-stone-50">
                      <span>Initial (at sale)</span>
                      <span className="font-medium">{fmtMoney(selected.initial_collected)}</span>
                    </div>
                  )}
                  {payments.map((p) => (
                    <div key={p.id || p._id} className="flex justify-between items-center text-xs px-2 py-1.5 rounded hover:bg-stone-50">
                      <div>
                        <span className="text-stone-500">{p.payment_date}</span>
                        {p.payment_method && <span className="ml-2 text-stone-400">· {p.payment_method}</span>}
                        {p.notes && <span className="ml-2 italic text-stone-400">{p.notes}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{fmtMoney(p.amount)}</span>
                        <button onClick={() => deletePayment(p.id || p._id)} className="text-red-500 hover:text-red-700" data-testid={`ar-delete-payment-${p.id || p._id}`}><Trash2 size={11} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}><X size={14} /> Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
